import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Seo from '../components/Seo';
import ScrollReveal from '../components/ScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';
import { servicePages, serviceSlugs, ServiceSlug } from '../data/servicePages';
import { consultationWhatsAppUrl } from '../data/whatsapp';
import './CountryPage.css';

interface ServicePageProps {
  slug: ServiceSlug;
}

const WHATSAPP_URL = consultationWhatsAppUrl();

const ServicePage: React.FC<ServicePageProps> = ({ slug }) => {
  const { language } = useLanguage();
  const content = servicePages[slug]?.[language];
  const actionsRef = useRef<HTMLDivElement>(null);
  const [shimmer, setShimmer] = useState(false);

  useEffect(() => {
    const node = actionsRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShimmer(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [slug]);

  if (!content) {
    return <Navigate to="/" replace />;
  }

  const path = `/services/${slug}`;
  const related = serviceSlugs.filter((item) => item !== slug);
  const labels = {
    ar: { home: 'الشراع', services: 'خدماتنا', related: 'خدمات أخرى', quote: content.quoteCta },
    en: { home: 'Al Shiraa', services: 'Services', related: 'Other services', quote: content.quoteCta },
    de: { home: 'Al Shiraa', services: 'Leistungen', related: 'Weitere Leistungen', quote: content.quoteCta },
  }[language];

  return (
    <article className="country-page">
      <Seo
        title={content.title}
        description={content.description}
        path={path}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: content.h1,
          serviceType: content.name,
          provider: {
            '@type': 'LocalBusiness',
            name: 'شركة الشراع للتخليص الجمركي',
            url: 'https://alshiraa-customs.com/',
            telephone: '+963933612861',
          },
          url: `https://alshiraa-customs.com${path}`,
          description: content.description,
          areaServed: 'Syria',
        }}
      />
      <div className="container country-page-inner">
        <div className="country-actions country-actions-top" ref={actionsRef}>
          <a
            className={`btn btn-primary btn-shimmer ${shimmer ? 'is-shimmering' : ''}`}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {content.cta}
          </a>
          <Link className={`btn btn-secondary btn-shimmer ${shimmer ? 'is-shimmering' : ''}`} to="/#quick-quote">
            {labels.quote}
          </Link>
        </div>
        <p className="country-breadcrumb">
          <Link to="/">{labels.home}</Link>
          <span> / </span>
          <Link to="/#services">{labels.services}</Link>
          <span> / </span>
          <span>{content.name}</span>
        </p>
        <h1 className="country-heading">{content.h1}</h1>
        <p className="country-intro">{content.intro}</p>
        {content.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        <h2>{content.benefitsTitle}</h2>
        <ScrollReveal variant="fade-up" asStagger>
          <ul className="country-crossings">
            {content.benefits.map((item, index) => (
              <li
                key={item}
                className="stagger-item"
                style={{ transitionDelay: `${index * 70}ms` }}
              >
                <span className="country-list-dot" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </ScrollReveal>
        <div className="country-actions">
          <a
            className={`btn btn-primary btn-shimmer ${shimmer ? 'is-shimmering' : ''}`}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {content.cta}
          </a>
          <Link className="btn btn-secondary" to="/#quick-quote">
            {labels.quote}
          </Link>
          <Link className="btn btn-secondary" to="/#contact">
            {language === 'ar' ? 'نموذج التواصل' : language === 'en' ? 'Contact form' : 'Kontaktformular'}
          </Link>
        </div>
        <h2>{labels.related}</h2>
        <ScrollReveal variant="fade-scale" asStagger>
          <div className="country-related">
            {related.map((item, index) => (
              <Link
                key={item}
                className="country-related-card interactive-lift stagger-item"
                to={`/services/${item}`}
                style={{ transitionDelay: `${index * 55}ms` }}
              >
                {servicePages[item][language].name}
              </Link>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </article>
  );
};

export default ServicePage;
